/**
 * Phase 83z.23 — Error reporter FAB + modal.
 *
 * Floating button bottom-right, only visible when the in-memory buffer
 * has ≥1 entry. Badge count = total entries. Tap → modal preview where
 * the user can pick which error to report, add a description, and submit.
 *
 * UX choices (not destructive):
 *   • Persistent across navigation (mounted once in App).
 *   • Does NOT auto-open. Pure "you-tell-us" model.
 *   • Submitting clears the buffer to avoid duplicate reports.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, m as motion } from "framer-motion";
import { toast } from "sonner";
import { Warning, X, PaperPlaneTilt, Trash } from "@phosphor-icons/react";
import {
  subscribeErrors,
  clearErrors,
  submitErrorReport,
} from "../lib/errorReporter";
import { useAuth } from "../contexts/AuthContext";

// Phase 85.A.18 — Chỉ admin/trọng tài/BTC mới thấy FAB báo lỗi. VĐV và guest
// không cần thấy (UX clean, không gây hoang mang).
const PRIVILEGED_ROLES = new Set([
  "super_admin",
  "admin",
  "organizer",
  "staff",
  "referee",
]);

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `${diff}s trước`;
    if (diff < 3600) return `${Math.floor(diff / 60)} phút trước`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
    return new Date(iso).toLocaleString("vi-VN");
  } catch {
    return "—";
  }
}

export default function ErrorReporterFab() {
  const { user } = useAuth();
  const location = useLocation();
  const [errors, setErrors] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const dismissedRef = useRef(new Set());

  useEffect(() => {
    return subscribeErrors((next) => {
      setErrors(next);
      // Default-pick the latest error when the modal is opened.
      setSelectedId((prev) => prev || next[0]?.id || null);
    });
  }, []);

  // Phase 85.A.18 — Hide FAB hoàn toàn cho VĐV/guest. Buffer vẫn ghi log
  // ngầm để gửi BE (server-side analytics), nhưng UI không pop-up gì cả.
  const isPrivileged = (user?.roles || []).some((r) => PRIVILEGED_ROLES.has(r));
  if (!isPrivileged) return null;

  // v16.80 — Ẩn FAB báo lỗi trên tab Bảng đấu admin (nhường chỗ cho FAB
  // "Phân bổ"). Các trang khác vẫn giữ nguyên.
  const isBracketTab =
    location.pathname.startsWith("/admin/tournaments/") &&
    new URLSearchParams(location.search).get("tab") === "bracket";
  if (isBracketTab) return null;

  // Don't render the FAB until we have at least 1 non-dismissed error.
  const visible = errors.some((e) => !dismissedRef.current.has(e.id));
  if (!visible) return null;

  const selected = errors.find((e) => e.id === selectedId) || errors[0];

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    const res = await submitErrorReport({
      message: selected.message,
      description: description.trim() || null,
      context: {
        timestamp: selected.timestamp,
        url: selected.url,
        source: selected.source,
        description_inline: selected.description,
      },
    });
    setSubmitting(false);
    if (res.ok) {
      toast.success("Đã gửi báo cáo cho BTC. Cảm ơn anh/chị!", {
        duration: 4000,
      });
      clearErrors();
      setDescription("");
      setModalOpen(false);
    } else {
      toast.error("Không gửi được — vui lòng thử lại sau ít phút.");
    }
  };

  const handleDismissAll = () => {
    errors.forEach((e) => dismissedRef.current.add(e.id));
    clearErrors();
    setModalOpen(false);
  };

  return (
    <>
      {/* FAB */}
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        data-testid="error-reporter-fab"
        className="fixed right-4 z-[140] inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#FF8A00] hover:bg-[#FF9A20] shadow-[0_8px_28px_rgba(255,138,0,0.45)] transition-transform active:scale-95"
        style={{
          // Phase 84.M.107 — bottom dùng max(env, --pl-safe-bottom) + 80px buffer
          // cho bottom nav (5rem ~ 80px) để không bị che bởi gesture bar Android.
          bottom: "calc(max(env(safe-area-inset-bottom, 0px), var(--pl-safe-bottom)) + 80px)",
        }}
        title={`${errors.length} lỗi gần đây — bấm để báo BTC`}
        aria-label="Báo lỗi cho BTC"
      >
        <Warning size={20} weight="fill" className="text-black" />
        <span
          className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-black flex items-center justify-center"
          data-testid="error-reporter-badge"
        >
          {errors.length > 9 ? "9+" : errors.length}
        </span>
      </button>

      {/* Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-md p-2 sm:p-4"
            onClick={() => setModalOpen(false)}
            data-testid="error-reporter-modal"
          >
            <motion.div
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 24, opacity: 0 }}
              transition={{ duration: 0.22 }}
              className="relative w-full max-w-lg max-h-[90dvh] overflow-y-auto bg-[#0F1623] border border-white/12 rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b border-white/8 bg-[#0F1623]/95 backdrop-blur">
                <div className="inline-flex items-center gap-2">
                  <Warning size={18} weight="fill" className="text-[#FF8A00]" />
                  <h3 className="text-[14px] font-black text-white">
                    Báo lỗi cho BTC
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="text-white/55 hover:text-white p-1"
                  data-testid="error-reporter-close"
                >
                  <X size={16} weight="bold" />
                </button>
              </div>

              {/* Body */}
              <div className="px-4 py-4 space-y-4">
                <p className="text-[11.5px] text-white/65 leading-relaxed">
                  Hệ thống ghi nhận {errors.length} thông báo lỗi gần đây trong
                  phiên này. Chọn lỗi muốn báo + mô tả ngắn việc anh/chị đang
                  làm, BTC sẽ kiểm tra và khắc phục.
                </p>

                {/* Error list */}
                <div className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1">
                  {errors.map((err) => (
                    <button
                      key={err.id}
                      type="button"
                      onClick={() => setSelectedId(err.id)}
                      data-testid={`error-item-${err.id}`}
                      className={`w-full text-left p-2.5 rounded-lg border transition ${
                        selected?.id === err.id
                          ? "border-[#FF8A00]/60 bg-[#FF8A00]/8"
                          : "border-white/8 bg-white/3 hover:bg-white/6"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <input
                          type="radio"
                          checked={selected?.id === err.id}
                          readOnly
                          className="accent-[#FF8A00] mt-0.5 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-[12px] font-bold text-white truncate">
                            {err.message}
                          </div>
                          <div className="text-[10px] text-white/45 mt-0.5 flex flex-wrap items-center gap-x-2">
                            <span>{timeAgo(err.timestamp)}</span>
                            {err.source && <span>· {err.source}</span>}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Description */}
                <div>
                  <label className="text-[10.5px] font-bold uppercase tracking-wider text-white/60 block mb-1.5">
                    Anh/chị đang làm gì khi lỗi xảy ra? (tuỳ chọn)
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value.slice(0, 500))}
                    rows={3}
                    placeholder="VD: bấm Thanh toán giải Karinox xong thấy báo lỗi đỏ..."
                    className="w-full bg-[#0A1220] border border-white/10 rounded-lg px-3 py-2 text-[12.5px] text-white placeholder:text-white/30 resize-none focus:border-[#FF8A00]/60 focus:outline-none"
                    data-testid="error-reporter-description"
                  />
                  <div className="text-right text-[10px] text-white/35 mt-0.5">
                    {description.length}/500
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div
                className="sticky bottom-0 flex items-center justify-between gap-2 px-4 py-3 border-t border-white/8 bg-[#0F1623]/95 backdrop-blur"
                style={{ paddingBottom: "calc(0.75rem + max(env(safe-area-inset-bottom, 0px), var(--pl-safe-bottom)))" }}
              >
                <button
                  type="button"
                  onClick={handleDismissAll}
                  className="inline-flex items-center gap-1.5 text-[11px] text-white/50 hover:text-white/80 px-2 py-1.5"
                  data-testid="error-reporter-dismiss-all"
                >
                  <Trash size={12} weight="bold" /> Bỏ qua hết
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || !selected}
                  className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-[#FF8A00] hover:bg-[#FF9A20] disabled:opacity-50 disabled:cursor-not-allowed text-black text-[12.5px] font-black transition"
                  data-testid="error-reporter-submit"
                >
                  <PaperPlaneTilt size={13} weight="bold" />
                  {submitting ? "Đang gửi..." : "Gửi báo cáo"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
